import React, { useMemo } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { formatMoney, formatShortDate } from './analyticsHelpers';

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 16,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
};

export const RevenueChart = ({ data, loading }) => {
  const total = useMemo(() => {
    if (!Array.isArray(data)) return 0;
    return data.reduce((acc, row) => acc + (row.revenue || 0), 0);
  }, [data]);
  const totalBookings = useMemo(() => {
    if (!Array.isArray(data)) return 0;
    return data.reduce((acc, row) => acc + (row.transactions ?? row.bookings ?? 0), 0);
  }, [data]);

  const chartData = useMemo(() => (data || []).map((row) => ({ ...row, dayLabel: formatShortDate(row.day) })), [data]);

  return (
    <div style={cardStyle} data-testid="revenue-chart-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div>
          <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, margin: 0, color: '#000' }}>Allure revenue</h3>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: '#888' }}>Clean platform profit — guest, Viva, venue, host & pool already subtracted</p>
        </div>
        <div style={{ fontSize: 12, color: '#666' }}>
          Total: <strong data-testid="revenue-chart-total" style={{ color: '#00B341', fontSize: 14 }}>{formatMoney(total)}</strong>
          <span style={{ margin: '0 8px', color: '#ccc' }}>·</span>
          <strong data-testid="revenue-chart-transactions">{totalBookings}</strong> transactions
        </div>
      </div>
      <div style={{ width: '100%', height: 220 }} data-testid="revenue-chart">
        {loading && (!chartData || chartData.length === 0) ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>Loading…</div>
        ) : (
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00B341" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#00B341" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
              <XAxis dataKey="dayLabel" tick={{ fontSize: 10, fill: '#999' }} />
              <YAxis tick={{ fontSize: 10, fill: '#999' }} tickFormatter={(value) => `€${value}`} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #EEE', fontSize: 12 }}
                formatter={(value, name) => (name === 'revenue' ? [formatMoney(value), 'Allure net'] : [value, 'Transactions'])}
                labelStyle={{ fontWeight: 700, color: '#333' }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#00B341" strokeWidth={2} fill="url(#revenueFill)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
